import {
  EMobilityChargingCardsDataModel,
  EMobilityChargingCardsResponse,
  EMobilityVehiclesResponse,
} from '../models/e-mobility-charging-cards-models'
import { chargingCardsDtoToModel } from '../transformers/charging-cards-dto-to-model.transformer'
import { useFetch } from '@e3dc-react/shell/Hooks/useFetch'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchWithResult } from '@e3dc-react/shell/Types/api'

import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useFarmContext } from 'src/Elements/Farm'
import { usePortalQueries } from 'src/Libs/api'

interface useEMobilityChargingCardsHook {
  data: EMobilityChargingCardsDataModel[] | undefined
  isLoading: boolean
  isError: boolean
}

export const useEMobilityChargingCards = (storageId: string): useEMobilityChargingCardsHook => {
  const authFetch = useAuthFetchWithResult()
  const { t } = useCustomTranslation()
  const { isLoading: isFarmLoading, isError: isFarmError, isFarm, farmClients } = useFarmContext()

  const [vehiclesResponse, isVehiclesLoading, , isVehiclesError] = useFetch<EMobilityVehiclesResponse>({
    url: `e-mobility/${storageId}/cars`,
    endpoint: process.env.REACT_APP_EMOBILITY_ENDPOINT,
  })

  const farmStorages = farmClients?.map(client => client.storageId) || []
  const storagesToFetchFrom = isFarm ? farmStorages : [storageId]

  const chargeCardQueries = usePortalQueries(
    storagesToFetchFrom.map(storage => {
      return {
        queryFn: () => fetchChargeCardsByStorage(storage, authFetch),
        queryKey: ['chargeCards', storage],
        enabled: storagesToFetchFrom.length > 0,
        refetchOnMount: true,
      }
    })
  )
  const chargeCardsLoading = chargeCardQueries.some(queryResult => queryResult.isLoading)
  const chargeCardsError = chargeCardQueries.some(queryResult => queryResult.isError)

  const mergedChargeCardResponses = chargeCardQueries
    .map(queryResult => chargingCardsDtoToModel(queryResult.data ?? undefined, vehiclesResponse, t))
    .flatMap(queryResult => (queryResult ? queryResult : []))

  return {
    data: mergedChargeCardResponses,
    isLoading: isFarmLoading || isVehiclesLoading || chargeCardsLoading,
    isError: isFarmError || chargeCardsError || isVehiclesError,
  }
}

const fetchChargeCardsByStorage = (storageId: string, authFetch: AuthFetchWithResult): Promise<EMobilityChargingCardsResponse> => {
  return authFetch<EMobilityChargingCardsResponse>({
    path: `e-mobility/${storageId}/cards`,
    endpoint: process.env.REACT_APP_EMOBILITY_ENDPOINT,
  })
}
